import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react'
import {
  useMidiMappingStore,
  selectBindingsFor,
  type ControlDescriptor,
  type ControlKind,
  type MidiBinding,
} from '@/stores/midiMappingStore'

interface UseMidiMappableOpts {
  /** Stable control id. Follow `section.control[.index]` dot-notation.
   *  Examples: `"transport.play"`, `"sequencer.bpm"`, `"pad.3.trigger"`. */
  id: string
  kind: ControlKind
  label: string
  /** Required for `kind: 'continuous'`; must bracket `setValue`'s
   *  expected input. */
  min?: number
  max?: number
  /** Required for `kind: 'discrete'`. */
  values?: readonly string[]
  /** Drive setter for 'toggle' | 'continuous' | 'discrete'. */
  setValue?: (v: number | boolean | string) => void
  /** Drive trigger for 'trigger' (momentary buttons, pad hits). */
  trigger?: () => void
}

export interface UseMidiMappableResult<T extends HTMLElement = HTMLElement> {
  /** Attach to the element you want Learn / Reveal to target.
   *  Pass directly as the `ref` prop on a native element:
   *  `<button ref={mapping.ref}>`. Dispatcher reads `ref.current`
   *  inside event handlers / RAF loops, never during render. */
  ref: RefObject<T>
  /** True while this control is currently armed for Learn. */
  isLearnTarget: boolean
  /** True while MIDI Learn mode is active. Use to dim or decorate
   *  the control for discoverability. */
  isLearnMode: boolean
  /** Current bindings for this control (persisted). */
  bindings: readonly MidiBinding[]
  /**
   * Call from the element's `onClick` (or `onPointerDown` for pads).
   * If Learn mode is active:
   *   - preventDefault + stopPropagation on the event
   *   - arm this control as the next learn target
   *   - return `true` so the caller's normal handler short-circuits
   * Otherwise: returns `false` — caller proceeds normally.
   */
  armForLearn: (e?: {
    preventDefault?: () => void
    stopPropagation?: () => void
  }) => boolean
}

/**
 * Registers a UI control in the MIDI mapping registry so it can be
 * Learn-target-ed, Shift+M-reveal-ed, and driven by incoming MIDI
 * messages through `GlobalMidiProvider`.
 *
 * The hook keeps a single `ControlDescriptor` alive for the lifetime
 * of the mount, with stable callbacks that read the *latest* props
 * via refs. This means the component can pass inline arrow functions
 * without re-registering on every render.
 *
 * On unmount the descriptor is removed — bindings stay persisted so
 * they reattach on the next mount.
 */
export function useMidiMappable<T extends HTMLElement = HTMLElement>(
  opts: UseMidiMappableOpts,
): UseMidiMappableResult<T> {
  const registerControl = useMidiMappingStore(s => s.registerControl)
  const unregisterControl = useMidiMappingStore(s => s.unregisterControl)
  const setLearnTarget = useMidiMappingStore(s => s.setLearnTarget)
  const learnMode = useMidiMappingStore(s => s.learnMode)
  const isLearnTarget = useMidiMappingStore(s => s.learnTargetId === opts.id)
  const bindings = useMidiMappingStore(
    useMemo(() => selectBindingsFor(opts.id), [opts.id]),
  )

  const elementBox = useRef<T>(null)
  const setValueRef = useRef(opts.setValue)
  const triggerRef = useRef(opts.trigger)
  const labelRef = useRef(opts.label)
  const minRef = useRef(opts.min)
  const maxRef = useRef(opts.max)
  const valuesRef = useRef(opts.values)
  // Keep the refs in sync with the latest props. Writing during
  // render violates React's rules (and the React Compiler lint);
  // a deps-free useEffect runs after every render which is exactly
  // what we need for "snapshot latest prop into ref" semantics.
  useEffect(() => {
    setValueRef.current = opts.setValue
    triggerRef.current = opts.trigger
    labelRef.current = opts.label
    minRef.current = opts.min
    maxRef.current = opts.max
    valuesRef.current = opts.values
  })

  useEffect(() => {
    // Build descriptor with callbacks that read from refs, so inline
    // arrow-fn props in the caller don't churn the registry.
    const descriptor: ControlDescriptor = {
      id: opts.id,
      kind: opts.kind,
      get label() {
        return labelRef.current
      },
      get min() {
        return minRef.current
      },
      get max() {
        return maxRef.current
      },
      get values() {
        return valuesRef.current
      },
      setValue: v => setValueRef.current?.(v),
      trigger: () => triggerRef.current?.(),
      getElement: () => elementBox.current,
    }
    registerControl(descriptor)
    return () => unregisterControl(opts.id)
  }, [opts.id, opts.kind, registerControl, unregisterControl])

  const armForLearn = useCallback(
    (e?: {
      preventDefault?: () => void
      stopPropagation?: () => void
    }): boolean => {
      const { learnMode: lm } = useMidiMappingStore.getState()
      if (!lm) return false
      e?.preventDefault?.()
      e?.stopPropagation?.()
      setLearnTarget(opts.id)
      return true
    },
    [opts.id, setLearnTarget],
  )

  return {
    ref: elementBox,
    isLearnTarget,
    isLearnMode: learnMode,
    bindings,
    armForLearn,
  }
}
